import { Crown, Trophy, Download, Copy, Share2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export default function DraftResults({ state, me }) {
  const { draft, participants } = state;

  // Sort teams by total spent (desc), spectators excluded.
  const teams = [...participants]
    .filter((p) => !p.is_spectator)
    .map((p) => ({ ...p, spent: p.roster.reduce((s, r) => s + r.price, 0) }))
    .sort((a, b) => b.spent - a.spent);

  const downloadCSV = () => {
    const rows = [["Team", "Golfer", "Price ($)"]];
    teams.forEach((t) => {
      if (t.roster.length === 0) {
        rows.push([t.username, "(no picks)", "0"]);
      } else {
        t.roster.forEach((r) => rows.push([t.username, r.name, String(r.price)]));
      }
    });
    rows.push([]);
    rows.push(["Team", "Total Spent", "Budget Left"]);
    teams.forEach((t) =>
      rows.push([t.username, String(t.spent), String(t.budget_remaining)])
    );
    const csv = rows
      .map((r) =>
        r
          .map((c) => {
            const s = String(c ?? "");
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          })
          .join(",")
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${draft.name.replace(/[^a-z0-9]+/gi, "_")}_${draft.code}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("CSV downloaded");
  };

  const buildSummaryText = () => {
    const lines = [];
    lines.push(`🏌️  ${draft.name}  ·  Draft #${draft.code}`);
    lines.push(
      `Budget $${draft.budget} · Roster ${draft.roster_size} · ${participants.length} teams`
    );
    lines.push("");
    teams.forEach((t, i) => {
      lines.push(
        `${i + 1}. ${t.username}  —  $${t.spent} spent  ($${t.budget_remaining} left)`
      );
      t.roster.forEach((r) => lines.push(`     • ${r.name}  $${r.price}`));
      lines.push("");
    });
    lines.push("— via The Palm Breezy Open Draft Room");
    return lines.join("\n");
  };

  const copySummary = async () => {
    try {
      await navigator.clipboard.writeText(buildSummaryText());
      toast.success("Summary copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  const shareNative = async () => {
    const text = buildSummaryText();
    if (navigator.share) {
      try {
        await navigator.share({ title: draft.name, text });
        return;
      } catch {
        // user dismissed or unsupported – fall back to copy
      }
    }
    copySummary();
  };

  return (
    <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 py-10 space-y-8">
      <div className="bg-pbo-green text-pbo-cream rounded-3xl p-8 sm:p-12 relative overflow-hidden">
        <div className="absolute inset-0 pbo-dotted opacity-30" />
        <div className="relative text-center">
          <div className="inline-flex items-center gap-2 text-pbo-gold-soft text-[10px] uppercase tracking-[0.3em] font-semibold">
            <Trophy className="w-3.5 h-3.5" /> Draft Complete
          </div>
          <h1 className="font-serif text-4xl sm:text-6xl mt-2">{draft.name}</h1>
          <p className="text-pbo-cream/70 mt-4">
            Rosters are locked. Tee it up — may the best foursome win.
          </p>

          <div className="mt-7 flex items-center justify-center gap-2 sm:gap-3 flex-wrap">
            <Button
              onClick={downloadCSV}
              className="bg-pbo-gold text-pbo-ink hover:bg-pbo-gold-soft font-semibold h-11 px-5 rounded-full"
              data-testid="download-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
            <Button
              onClick={copySummary}
              variant="outline"
              className="border-pbo-gold/40 text-pbo-cream hover:bg-pbo-green-deep hover:text-pbo-cream h-11 px-5 rounded-full bg-transparent"
              data-testid="copy-summary-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              Copy summary
            </Button>
            <Button
              onClick={shareNative}
              variant="outline"
              className="border-pbo-gold/40 text-pbo-cream hover:bg-pbo-green-deep hover:text-pbo-cream h-11 px-5 rounded-full bg-transparent"
              data-testid="share-results-btn"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share
            </Button>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4" data-testid="results-teams">
        {teams.map((t, idx) => (
          <div
            key={t.id}
            className={`bg-white border rounded-2xl p-6 ${
              me?.id === t.id ? "border-pbo-gold" : "border-pbo-border"
            }`}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-mono text-xs text-pbo-muted">
                  #{String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-xl text-pbo-green truncate">
                  {t.username}
                </h3>
                {t.is_host && <Crown className="w-4 h-4 text-pbo-gold" />}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-pbo-muted">
                  Spent / Left
                </div>
                <div className="font-mono text-sm">
                  <span className="text-pbo-green font-semibold">${t.spent}</span>
                  <span className="text-pbo-muted"> / ${t.budget_remaining}</span>
                </div>
              </div>
            </div>
            <div className="space-y-1 text-sm">
              {t.roster.map((r) => (
                <div
                  key={r.player_id}
                  className="flex justify-between items-baseline border-b border-pbo-border/60 pb-1"
                >
                  <span className="text-pbo-ink/85">{r.name}</span>
                  <span className="font-mono text-pbo-gold font-semibold">
                    ${r.price}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="text-center">
        <Link to="/">
          <Button
            className="bg-pbo-green text-pbo-cream hover:bg-pbo-green-deep h-12 px-6 rounded-full font-semibold"
            data-testid="results-back-home-btn"
          >
            Back to the clubhouse
          </Button>
        </Link>
      </div>
    </div>
  );
}
